import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';

import DnaBackground from '../components/DnaBackground';
import TypeAvatar from '../components/TypeAvatar';
import Seo from '../components/Seo';
import { getResults } from '../utils/profileCalculator';
import { founderTypes } from '../data/founderTypes';
import { applyTypeTheme, buildTypeCode, clearTypeTheme } from '../utils/founderIdentity';
import { formatFounderTitle } from '../utils/textUtils';
import { trackEvent } from '../utils/analytics';
import { REVEAL } from '../data/productCopy';

const Page = styled.div`
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 2rem 1.25rem;
  position: relative;
  z-index: 1;
`;

const Card = styled(motion.div)`
  width: 100%;
  max-width: 520px;
  text-align: center;
  padding: 2.5rem 1.75rem;
  border-radius: 20px;
  background: rgba(18, 18, 28, 0.85);
  border: 1px solid rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(16px);
  box-shadow: 0 24px 60px rgba(0, 0, 0, 0.35);
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 3px;
    background: var(--type-accent, var(--primary-color));
  }
`;

const Eyebrow = styled.p`
  font-size: 0.85rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.14em;
  color: var(--type-accent, var(--primary-color));
  margin-bottom: 1.25rem;
`;

const Title = styled.h1`
  font-family: var(--font-display);
  font-size: clamp(1.85rem, 5vw, 2.35rem);
  font-weight: 800;
  color: var(--text-primary);
  margin: 1rem 0 0.5rem;
  line-height: 1.15;
  letter-spacing: -0.03em;
`;

const Tagline = styled.p`
  font-size: 1.15rem;
  color: var(--text-secondary);
  line-height: 1.55;
  margin: 0 0 1.5rem;
`;

const TypeCode = styled.div`
  display: inline-block;
  font-size: 1.1rem;
  font-weight: 800;
  letter-spacing: 0.2em;
  color: var(--type-accent, var(--primary-color));
  padding: 0.5rem 1rem;
  border-radius: 10px;
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.12);
  margin-bottom: 1.25rem;
`;

const PercentPill = styled.span`
  display: inline-block;
  margin-top: 0.5rem;
  font-size: 0.95rem;
  color: rgba(255, 255, 255, 0.7);
`;

const PrimaryButton = styled(motion.button)`
  width: 100%;
  max-width: 320px;
  margin: 2rem auto 0;
  display: block;
  padding: 1rem 1.5rem;
  border-radius: 12px;
  font-weight: 700;
  font-size: 1.05rem;
  color: #0C0F14;
  background: var(--type-accent, var(--primary-color));
`;

const GhostButton = styled.button`
  margin-top: 1rem;
  background: none;
  border: none;
  color: var(--text-secondary);
  font-size: 0.95rem;
  cursor: pointer;
  text-decoration: underline;
  text-underline-offset: 3px;
`;

const DotRow = styled.div`
  display: flex;
  justify-content: center;
  gap: 0.5rem;
  margin-top: 1.5rem;
`;

const Dot = styled.span`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: ${(props) => (props.$active ? 'var(--type-accent, var(--primary-color))' : 'rgba(255,255,255,0.25)')};
`;

const ResultsReveal = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [profile, setProfile] = useState(null);

  useEffect(() => {
    const data = getResults();
    const p = data?.profile;
    if (!p?.primaryType) {
      navigate('/', { replace: true });
      return;
    }
    setProfile(p);
    applyTypeTheme(p.primaryType);
    trackEvent('results_reveal_started', { primaryType: p.primaryType });
  }, [navigate]);

  useEffect(() => () => clearTypeTheme(), []);

  if (!profile?.primaryType) return null;

  const primary = founderTypes[profile.primaryType];
  const secondary = profile.secondaryType ? founderTypes[profile.secondaryType] : null;
  const typeCode = buildTypeCode(profile.primaryType, profile.secondaryType);
  const totalSteps = secondary ? 3 : 2;
  const isBlendStep = secondary ? step === 2 : step === 1;

  const finishReveal = () => {
    sessionStorage.setItem('founderDna_revealSeen', '1');
    trackEvent('results_reveal_completed', {
      primaryType: profile.primaryType,
      secondaryType: profile.secondaryType,
    });
    navigate('/results');
  };

  const goNext = () => {
    if (step === 0) {
      if (secondary) {
        applyTypeTheme(profile.secondaryType);
        setStep(1);
      } else {
        setStep(1);
      }
      return;
    }
    if (step === 1 && secondary) {
      applyTypeTheme(profile.primaryType);
      setStep(2);
      return;
    }
    finishReveal();
  };

  return (
    <Page>
      <Seo
        title="Your operating profile"
        description="Your founder operating profile reveal: primary type, blend, and full playbook."
        path="/results/reveal"
      />
      <DnaBackground />

      <AnimatePresence mode="wait">
        {step === 0 && (
          <Card key="s0" initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -16 }}>
            <Eyebrow>Your primary operating mode</Eyebrow>
            <TypeAvatar type={primary} size={140} showCode />
            <Title>{formatFounderTitle(primary.title)}</Title>
            <Tagline>{primary.tagline}</Tagline>
            <PercentPill>{profile.percentages?.[profile.primaryType] || 0}% of your answers</PercentPill>
            <PrimaryButton type="button" onClick={goNext} whileTap={{ scale: 0.98 }}>
              {secondary ? 'Continue' : 'Next'}
            </PrimaryButton>
            <GhostButton type="button" onClick={finishReveal}>{REVEAL.skipCta}</GhostButton>
            <DotRow>{Array.from({ length: totalSteps }).map((_, i) => <Dot key={i} $active={i === 0} />)}</DotRow>
          </Card>
        )}

        {step === 1 && secondary && (
          <Card key="s1" initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -16 }}>
            <Eyebrow>You also lead with</Eyebrow>
            <TypeAvatar type={secondary} size={120} showCode />
            <Title>{formatFounderTitle(secondary.title)}</Title>
            <Tagline>{secondary.tagline}</Tagline>
            <PercentPill>{profile.percentages?.[profile.secondaryType] || 0}% secondary influence</PercentPill>
            <PrimaryButton type="button" onClick={goNext} whileTap={{ scale: 0.98 }}>See your full blend</PrimaryButton>
            <DotRow>{Array.from({ length: totalSteps }).map((_, i) => <Dot key={i} $active={i === 1} />)}</DotRow>
          </Card>
        )}

        {isBlendStep && (
          <Card key="s2" initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -16 }}>
            <Eyebrow>{REVEAL.finalEyebrow}</Eyebrow>
            <TypeCode>{typeCode}</TypeCode>
            <Title style={{ fontSize: '1.55rem' }}>
              {secondary
                ? `${formatFounderTitle(primary.title)} + ${formatFounderTitle(secondary.title)}`
                : formatFounderTitle(primary.title)}
            </Title>
            <Tagline>{REVEAL.finalTagline}</Tagline>
            <PrimaryButton type="button" onClick={finishReveal} whileTap={{ scale: 0.98 }}>
              {REVEAL.openProfileCta}
            </PrimaryButton>
            <DotRow>{Array.from({ length: totalSteps }).map((_, i) => <Dot key={i} $active={i === step} />)}</DotRow>
          </Card>
        )}
      </AnimatePresence>
    </Page>
  );
};

export default ResultsReveal;
